import React, { useState, useEffect, useRef } from 'react';
import {
Box,
Typography,
TextField,
Button,
Alert,
CircularProgress,
Chip,
IconButton,
Collapse,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
Badge,
InputAdornment,
Tooltip
} from '@mui/material';
import {
Send as SendIcon,
Info as InfoIcon,
Warning as WarningIcon,
Edit as EditIcon,
FilterList as FilterListIcon
} from '@mui/icons-material';
import { addDoc, collection, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { getApp } from 'firebase/app';
import { toast } from 'react-toastify';

const STREAMING_ENDPOINT = `https://us-central1-${getApp().options.projectId}.cloudfunctions.net/pocAutomatedAnswerStream`;

const QuestionForm = ({
  clientId,
  user,
  clientIsActive,
  clients,
  onQuestionSubmitted,
  onToggleFilterPanel,
  filtersActive,
  onStreamStart,
  onStreamChunk,
  onStreamPhase,
  onStreamDone,
  onStreamError,
  }) => {
  // Form state
  const [questionText, setQuestionText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPrivacyInfo, setShowPrivacyInfo] = useState(false);

  // Governance flag state
  const [governanceFlag, setGovernanceFlag] = useState(null);
  const [editedQuestion, setEditedQuestion] = useState('');
  const [resubmitting, setResubmitting] = useState(false);

    const activeStreamAbortRef = useRef(null);

  useEffect(() => {
    return () => {
      if (activeStreamAbortRef.current) {
        activeStreamAbortRef.current.abort();
      }
    };
  }, []);

  // Get current client info
  const getCurrentClient = () => {
    if (clientId === 'ALL' || !clientId) return null;
    return clients?.find(c => c.id === clientId);
  };

  const currentClient = getCurrentClient();

  // Determine if the text box should be disabled
  const isTextBoxDisabled = clientId === 'ALL' || !clientIsActive;
  const canSubmit = clientId && clientId !== 'ALL' && clientIsActive && questionText.trim();

  // Build placeholder text
  const getPlaceholder = () => {
    if (clientId === 'ALL') return 'Select a specific client to ask a question...';
    if (!clientIsActive) return 'This client is inactive...';
    if (currentClient) return `Ask about ${currentClient.displayName || currentClient.name}...`;
    return 'What would you like to know about your business?';
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!canSubmit || submitting) return;

    const userId = user?.uid || user?.id;
    if (!userId) {
      toast.error('User not authenticated');
      return;
    }

    setSubmitting(true);
    const toastId = toast.loading("Submitting your question...");

    let newQuestionId = null;

    try {
      const currentClient = clients?.find(c => c.id === clientId);
      const questionData = {
        questionText: questionText.trim(),
        clientId: clientId,
        organizationId: currentClient?.organizationId || null,
        userId: userId,
        fcfoId: userId,
        userEmail: user.email,
        status: 'pending',
        aiStatus: 'processing',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        type: 'standard',
        category: 'general',
        answer: null,
        feedback: null,
        favoritedBy: [],
        userNotes: ""
      };

            const submittedQuestionText = questionText.trim();

      const docRef = await addDoc(collection(db, 'questions'), questionData);
      newQuestionId = docRef.id;

      toast.update(toastId, { render: "Question saved. Triggering AI analysis...", type: "info" });

      // Clear the input immediately for better UX, but DO NOT move onQuestionSubmitted.
      setQuestionText('');

      let streamingSucceeded = false;
      let receivedAnyStreamEvent = false;

      try {
        const currentUser = getAuth().currentUser;
        if (!currentUser) throw new Error('No authenticated user for streaming');

        const idToken = await currentUser.getIdToken();
        const abortController = new AbortController();
        activeStreamAbortRef.current = abortController;

        if (onStreamStart) onStreamStart(newQuestionId);

        const streamResponse = await fetch(STREAMING_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            userQuestion: submittedQuestionText,
            clientId,
            questionId: newQuestionId,
          }),
          signal: abortController.signal,
        });

        if (!streamResponse.ok || !streamResponse.body) {
          throw new Error(`Streaming HTTP ${streamResponse.status}`);
        }

        const reader = streamResponse.body.getReader();
        const decoder = new TextDecoder();
        let sseBuffer = '';
        let sseErrorMessage = null;
        let sseComplete = false;

        try {
          while (!sseComplete) {
            const { done, value } = await reader.read();
            if (done) break;

            sseBuffer += decoder.decode(value, { stream: true });

            // SSE events are separated by blank lines
            const events = sseBuffer.split('\n\n');
            sseBuffer = events.pop() || '';

            for (const rawEvent of events) {
              const lines = rawEvent
                .split('\n')
                .map(line => line.trimEnd())
                .filter(Boolean);

              const dataLine = lines.find(line => line.startsWith('data: '));
              if (!dataLine) continue;

              try {
                const event = JSON.parse(dataLine.slice(6));

                if (event.type === 'status' || event.type === 'provider' || event.type === 'chunk' || event.type === 'done') {
                  receivedAnyStreamEvent = true;
                }

                if (event.type === 'status' && onStreamPhase) {
                  onStreamPhase(event.phase);
                }

                if (event.type === 'chunk' && onStreamChunk) {
                  onStreamChunk(event.text || '');
                }

                if (event.type === 'error') {
                  sseErrorMessage = event.message || 'Streaming error';
                  sseComplete = true;
                  break;
                }

                if (event.type === 'done') {
                  sseComplete = true;
                  break;
                }
              } catch {
                // Ignore malformed event payloads
              }
            }
          }
        } finally {
          try {
            await reader.cancel();
          } catch {
            // ignore
          }
        }

        if (sseErrorMessage) {
          throw new Error(sseErrorMessage);
        }

        streamingSucceeded = true;
        if (onStreamDone) onStreamDone(newQuestionId);

        toast.update(toastId, {
          render: "Answer ready!",
          type: "success",
          isLoading: false,
          autoClose: 3000
        });

      } catch (streamErr) {
        console.warn('[QuestionForm] Streaming failed:', streamErr?.message || streamErr);

        // Important: only fallback if streaming never actually started.
        // If we already received SSE events, do NOT launch a second pipeline run.
        if (!receivedAnyStreamEvent) {
          if (onStreamError) onStreamError();

          const pocAutomatedAnswer = httpsCallable(functions, 'pocAutomatedAnswer', { timeout: 300000 });
          await pocAutomatedAnswer({
            userQuestion: submittedQuestionText,
            clientId,
            questionId: newQuestionId,
          });

          toast.update(toastId, {
            render: "AI processing started! Your answer will appear shortly.",
            type: "success",
            isLoading: false,
            autoClose: 5000
          });
        } else {
          if (onStreamPhase) onStreamPhase('stream_interrupted');

          toast.update(toastId, {
            render: "Live streaming was interrupted, but processing may still complete shortly.",
            type: "warning",
            isLoading: false,
            autoClose: 5000
          });
        }
      } finally {
        activeStreamAbortRef.current = null;
      }

      // Preserve existing callback timing semantics.
      if (onQuestionSubmitted) {
        onQuestionSubmitted();
      }

    } catch (error) {
        console.error("Error during question submission pipeline:", error);

        const details = error?.details;
        if (details?.governanceFlag) {
            toast.update(toastId, { render: "Your question was flagged for privacy review.", type: "warning", isLoading: false, autoClose: 5000 });
            if (newQuestionId) {
                const questionRef = doc(db, 'questions', newQuestionId);
                await updateDoc(questionRef, { status: 'governance_flagged', aiStatus: 'governance_flagged' });
            }
            setGovernanceFlag({
                ...details,
                questionId: newQuestionId,
                originalText: questionText.trim(),
            });
            setEditedQuestion(questionText.trim());
        } else {
            toast.update(toastId, { render: `An error occurred: ${error.message}`, type: "error", isLoading: false, autoClose: 5000 });
            if (newQuestionId) {
                const questionRef = doc(db, 'questions', newQuestionId);
                await updateDoc(questionRef, { status: 'error', aiStatus: 'error' });
            }
        }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit & resubmit from governance flag modal
  const handleResubmit = async () => {
    if (!editedQuestion.trim() || !governanceFlag?.questionId) return;

    setResubmitting(true);
    const toastId = toast.loading("Resubmitting your edited question...");

    try {
      const questionRef = doc(db, 'questions', governanceFlag.questionId);
      await updateDoc(questionRef, {
        questionText: editedQuestion.trim(),
        status: 'pending',
        aiStatus: 'processing',
        updatedAt: serverTimestamp(),
        governanceResubmitted: true,
        previousText: governanceFlag.originalText,
      });

      const pocAutomatedAnswer = httpsCallable(functions, 'pocAutomatedAnswer', { timeout: 300000 });
      await pocAutomatedAnswer({
        userQuestion: editedQuestion.trim(),
        clientId: clientId,
        questionId: governanceFlag.questionId,
      });

      toast.update(toastId, { render: "Question resubmitted successfully! Answer will appear shortly.", type: "success", isLoading: false, autoClose: 5000 });
      setGovernanceFlag(null);
      setEditedQuestion('');
      setQuestionText('');
      if (onQuestionSubmitted) onQuestionSubmitted();

    } catch (error) {
      console.error("Error resubmitting question:", error);
      if (error?.details?.governanceFlag) {
        toast.update(toastId, { render: "Still flagged. Please remove more sensitive information.", type: "warning", isLoading: false, autoClose: 5000 });
        setGovernanceFlag(prev => ({
          ...prev,
          ...error.details,
          originalText: prev.originalText,
        }));
        setEditedQuestion(editedQuestion.trim());
      } else {
        toast.update(toastId, { render: `Error: ${error.message}`, type: "error", isLoading: false, autoClose: 5000 });
      }
    } finally {
      setResubmitting(false);
    }
  };

  // Dismiss governance flag
  const handleDismissFlag = async () => {
    setGovernanceFlag(null);
    setEditedQuestion('');
  };

  // Handle Enter key (submit on Enter, newline on Shift+Enter)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box>
      {/* Compact alerts — shown above input bar */}
      {clientId === 'ALL' && (
        <Alert severity="info" sx={{ mb: 1, py: 0 }} variant="outlined">
          <Typography variant="caption">Select a specific client to ask a question.</Typography>
        </Alert>
      )}
      {!clientIsActive && clientId && clientId !== 'ALL' && (
        <Alert severity="warning" sx={{ mb: 1, py: 0 }} variant="outlined">
          <Typography variant="caption">This client is inactive. Questions cannot be submitted.</Typography>
        </Alert>
      )}

      {/* Privacy info — collapsible */}
      <Collapse in={showPrivacyInfo}>
        <Alert severity="info" sx={{ mb: 1 }} onClose={() => setShowPrivacyInfo(false)}>
          <Typography variant="caption">
            <strong>Your privacy is protected.</strong> Questions are automatically scanned for PII before AI processing.
            Company names, personal names, and sensitive data are replaced with placeholders.
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            <strong>Your conversation history is continuous.</strong> SavvaiEdge maintains a single ongoing thread per client that is automatically used as context for every new question — so your AI advisor always has the full picture of prior discussions, without you needing to repeat background.
          </Typography>
        </Alert>
      </Collapse>

      {/* Chat-style input bar */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        {/* Filter toggle button */}
        {onToggleFilterPanel && (
          <Tooltip title={filtersActive ? "Filters active — click to toggle" : "Search, filter & export"}>
            <IconButton
              onClick={onToggleFilterPanel}
              size="small"
              sx={{
                mb: 0.5,
                color: filtersActive ? 'primary.main' : 'text.secondary',
                bgcolor: filtersActive ? 'primary.50' : 'transparent',
                '&:hover': { bgcolor: filtersActive ? 'primary.100' : 'action.hover' }
              }}
            >
              <Badge
                variant="dot"
                color="primary"
                invisible={!filtersActive}
              >
                <FilterListIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        )}

        {/* Main text input */}
        <TextField
          multiline
          minRows={1}
          maxRows={6}
          fullWidth
          value={questionText}
          onChange={(e) => setQuestionText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={getPlaceholder()}
          disabled={isTextBoxDisabled}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '24px',
              bgcolor: 'grey.50',
              pr: 1,
              '& fieldset': {
                borderColor: 'grey.300',
              },
              '&:hover fieldset': {
                borderColor: 'grey.400',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
                borderWidth: 1,
              },
            },
            '& .MuiInputBase-input': {
              fontSize: '0.95rem',
              py: 1.2,
              '&::placeholder': {
                opacity: 0.5,
              }
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ ml: 0.5, alignSelf: 'flex-end', mb: 0.5 }}>
                {/* Privacy info icon */}
                <Tooltip title="Privacy information">
                  <IconButton
                    size="small"
                    onClick={() => setShowPrivacyInfo(prev => !prev)}
                    sx={{ mr: 0.5, color: 'text.disabled' }}
                  >
                    <InfoIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                {/* Send button */}
                <IconButton
                  onClick={handleSubmit}
                  disabled={!canSubmit || submitting}
                  size="small"
                  sx={{
                    bgcolor: canSubmit && !submitting ? 'primary.main' : 'grey.200',
                    color: canSubmit && !submitting ? 'white' : 'text.disabled',
                    width: 34,
                    height: 34,
                    '&:hover': {
                      bgcolor: canSubmit && !submitting ? 'primary.dark' : 'grey.200',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'grey.200',
                      color: 'text.disabled',
                    },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  {submitting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SendIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          inputProps={{ maxLength: 2000 }}
        />
      </Box>

      {/* Character count — only shown when typing */}
      {questionText.length > 0 && (
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: 'block', textAlign: 'right', mt: 0.3, mr: 1, fontSize: '0.7rem' }}
        >
          {questionText.length}/2000
        </Typography>
      )}

      {/* Governance Flag — Edit & Resubmit Modal (unchanged logic) */}
      <Dialog open={!!governanceFlag} onClose={handleDismissFlag} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.dark' }}>
          <WarningIcon color="warning" />
          Question Flagged for Privacy Review
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your question was flagged because it may contain sensitive information.
            {governanceFlag?.totalPiiFound > 0 && ` (${governanceFlag.totalPiiFound} item${governanceFlag.totalPiiFound !== 1 ? 's' : ''} detected)`}
          </Alert>

          {governanceFlag?.dlpFindings?.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Detected:
              </Typography>
              <Box display="flex" gap={0.5} sx={{ mt: 0.5 }}>
                {governanceFlag.dlpFindings.map((finding, idx) => (
                  <Chip
                    key={idx}
                    label={`${finding.infoType.replace(/_/g, ' ')}: ${finding.count}`}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          {governanceFlag?.sanitizedPreview && (
            <Box sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                After sanitization (items replaced with [person], [email], etc.):
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {governanceFlag.sanitizedPreview}
              </Typography>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Edit your question below to remove sensitive information, then resubmit. 
            Avoid including personal names, email addresses, or other identifying details.
          </Typography>

          <TextField
            multiline
            rows={4}
            fullWidth
            label="Edit Your Question"
            value={editedQuestion}
            onChange={(e) => setEditedQuestion(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            helperText={`${editedQuestion.length}/2000 characters`}
            sx={{
              '& .MuiInputBase-input': { fontSize: '1.1rem' },
              '& .MuiInputLabel-root': { fontSize: '1.1rem' }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDismissFlag} disabled={resubmitting}>
            Dismiss
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={resubmitting ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
            disabled={resubmitting || !editedQuestion.trim()}
            onClick={handleResubmit}
          >
            {resubmitting ? 'Resubmitting...' : 'Edit & Resubmit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuestionForm;